import React, { createContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext();

// Environment Variables
export const base_url = process.env.REACT_APP_API_URL;
export const app_url = process.env.REACT_APP_APP_URL;
export const AGENTID = process.env.REACT_APP_AGENT_ID;
export const AGENTKEY = process.env.REACT_APP_AGENT_KEY;
export const REFERRERURL = process.env.REACT_APP_REFERRER_URL;
export const BASEURL = process.env.REACT_APP_BASE_URL;

export const AuthProvider = ({ children }) => {
  // Payment and Withdrawal Status Mappings
  const PaymentStatusMap = { PENDING: 0, SUCCESS: 1, CANCELLED: 2 };
  const WITHDRAWAL_STATUS_MAP = { PENDING: 0, APPROVED: 1, DENIED: 2 };
  const navigate=useNavigate();
  const [userBalnc, setUserBalnc] = useState({ userTotalBal: 0, bonus: 0 })

  // Helper function to get cookie value
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
  };

  // The session itself is an HttpOnly cookie the page cannot read (#14);
  // the server sets rb_session=1 beside it so the UI knows one exists.
  const loginstatus = getCookie("rb_session");
  const [isAuthenticated, setIsAuthenticated] = useState(!!loginstatus);

  // Cookies can be blocked outright -- in-app browsers (Instagram, Facebook,
  // Telegram), strict privacy settings, embeds. Login used to close the
  // modal and simply not log anyone in; now the dialog can say why.
  const [cookiesBlocked] = useState(() => {
    try {
      if (typeof navigator !== "undefined" && navigator.cookieEnabled === false) return true;
      document.cookie = "rb_cookie_test=1; path=/; SameSite=Lax";
      const ok = document.cookie.indexOf("rb_cookie_test=1") !== -1;
      document.cookie = "rb_cookie_test=; max-age=0; path=/";
      return !ok;
    } catch (e) {
      return true;
    }
  });

  // Ends the session on the server (the page cannot delete an HttpOnly
  // cookie) and removes what the old client-written session left behind.
  const endServerSession = () =>
    axios
      .post(`${base_url}/api/webapi/logout`, {}, { withCredentials: true })
      .catch(() => {});

  // Everything we park in localStorage that belongs to the signed-in user.
  // Why: phone/username/balance/token are PII or auth material and must not
  // linger after sign-out (shared-device / XSS exposure). The header now seeds
  // its username chip from this store so the name paints before GetUserInfo
  // answers, which makes leftovers visible too -- a stale entry would show the
  // previous account's name to the next person who logs in on this device.
  const USER_SCOPED_KEYS = [
    "hasSeenLoginModal",
    "username",
    "userbalance",
    "phoneNumber",
    "phone",
    "game_token",
    "userDataCache",
  ];
  const clearUserScopedStorage = () =>
    USER_SCOPED_KEYS.forEach((k) => localStorage.removeItem(k));


 const inactivityTimer = useRef(null);
  // 14 minutes signed people out mid-session: watching a live match or an odds
  // page that refreshes itself is real use, but it fires no input events.
  const INACTIVITY_TIME = 60 * 60 * 1000;
  // mousemove/keydown/click/scroll never fire on a phone that is only being
  // tapped, so touch/pointer have to be here too or mobile counts down while
  // the player is using the site.
  const ACTIVITY_EVENTS = [
    "mousemove",
    "keydown",
    "click",
    "scroll",
    "touchstart",
    "touchmove",
    "pointerdown",
  ];

  const logoutAndRedirect = () => {
    endServerSession();
    document.cookie = "token=; max-age=0; path=/;";
    document.cookie = "auth=; max-age=0; path=/;";
    // rb_session is the flag the UI reads for "signed in". If the logout POST
    // above fails it stays at 1 and the app still looks logged in, so drop it
    // here as well -- unlike `auth` it is not HttpOnly, so this actually works.
    document.cookie = "rb_session=; max-age=0; path=/";
    setIsAuthenticated(false);
    // Same wipe as the explicit logout below -- an idle timeout ends the
    // session just as much as pressing the button does.
    clearUserScopedStorage();
    navigate("/", { replace: true });
  };

  const resetInactivityTimer = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(logoutAndRedirect, INACTIVITY_TIME);
  };

  // A backgrounded tab fires no activity events at all, so the countdown used
  // to run to zero behind the player's back and log them out while they were
  // reading another tab. Freeze it while hidden; coming back counts as activity.
  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    } else {
      resetInactivityTimer();
    }
  };

  const startTracking = () => {
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer)
    );
    // visibilitychange is fired at the document, not the window.
    document.addEventListener("visibilitychange", handleVisibilityChange);
    resetInactivityTimer();
  };

  useEffect(() => {
    if (isAuthenticated) startTracking();
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, resetInactivityTimer)
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated]);

  // Register Function
  const Register = (data, callback) => {
    axios
      .post(`${base_url}/api/webapi/register`, data, {
        headers: { Accept: "application/json" },
      })
      .then((response) => callback(response.data))
      .catch((error) =>
        callback({
          status: false,
          message:
            error.response?.data?.message ||
            error.message ||
            "Registration failed. Please try again.",
        })
      );
  };

  // Login Function
  const login = (data, callback) => {
    // console.log('lofinn',data);
    axios
      .post(`${base_url}/api/webapi/login`, data, {
        // withCredentials so the browser keeps the Set-Cookie when the API
        // is on another origin (local development: :3000 -> :3004).
        withCredentials: true,
        headers: { Accept: "application/json" },
      })
      .then((response) => {
        if (response.data.status) {
          // The server has set the session cookie. If rb_session did not
          // stick, this browser is dropping cookies: say so rather than
          // closing the dialog on a login that went nowhere.
          if (!getCookie("rb_session")) {
            callback({
              status: false,
              message:
                "Your browser is blocking cookies, so you can't stay logged in. Please enable cookies, or open this site in Chrome or Safari.",
            });
            return;
          }

          setIsAuthenticated(true);
          startTracking();
          callback(response.data);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) =>
        callback({
          status: false,
          message: error.response?.data?.message || error.message,
        })
      );
  };

  // Fetch User Data
const userdata = async (callback) => {
    try {
      const response = await axios.post(
        `${base_url}/api/webapi/GetUserInfo`,
        {},
        { withCredentials: true, headers: { Accept: "application/json" } }
      );
      const data = response.data;
      // setItem() stringifies, so a missing field would store the literal
      // "undefined" and the header would render that word as the user's name.
      const userDemoName = response?.data?.data?.username;
      const userPhone = response?.data?.data?.phone_user;
      if (userDemoName) localStorage.setItem("username", userDemoName);
      else localStorage.removeItem("username");
      if (userPhone) localStorage.setItem("phoneNumber", userPhone);
      else localStorage.removeItem("phoneNumber");
      setUserBalnc({ userTotalBal: data.data.money_user, bonus: data.bonus_money || 0 });
      callback(response.data);
      return response.data;
    } catch (error) {
      console.error("User data error:", error.message);
      callback(null);
      return null;
    }
  };

  // Change Password
  const changepassword = (data, callback) => {
    // The server reads the session from the cookie.
    const authdata = { ...data };
    axios
      // withCredentials was missing, so the session cookie never went with the
      // request and the server could not tell whose password to change.
      .post(`${base_url}/api/webapi/change/pass`, authdata, {
        withCredentials: true,
      })
      .then((response) => callback(response.data))
      .catch((error) =>
        callback({
          status: false,
          message: error.response?.data?.message || error.message,
        })
      );
  };
 

  // Logout Function
const logout = () => {
    endServerSession();
    document.cookie = `token=; max-age=0; path=/;`;
    document.cookie = `auth=; max-age=0; path=/;`;
    // See logoutAndRedirect: rb_session is what the UI trusts, so it has to go
    // even if the logout POST never reaches the server.
    document.cookie = "rb_session=; max-age=0; path=/";
    setIsAuthenticated(false);
    clearUserScopedStorage();
    navigate("/");
  }
  // Fetch Bank Details
  const getBankDetails = async () => {
    try {
      const response = await axios.post(
        `${base_url}/admin/manager/settings/get`,
        {},
        { withCredentials: true, headers: { Accept: "application/json" } }
      );
      return response.data.momo;
    } catch (error) {
      console.error("Bank details error:", error.message);
      return null;
    }
  };

  //fetch roles of logged in person
  const getRoles= async(auth, role)=> {
    try{
      const response= await axios.post(base_url+ '/api/webapi/admin/Rolelist',
        {},
        {
          headers:{
            Accept: 'application/json',
            Authorization: `${auth}`
          }
        }
      )

      const fullRoles= response.data.data;
      // console.log('full',fullRoles);

      const specifiedRole= fullRoles.filter((oneRole)=> Object.keys(oneRole).includes(role));

      return specifiedRole;
    }catch(err){
      console.log('err',err);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        cookiesBlocked,
        PaymentStatusMap,
        WITHDRAWAL_STATUS_MAP,
        Register,
        login,
        logout,
        userdata,
        changepassword,
        getBankDetails,
        setIsAuthenticated,
        getRoles,
        base_url,
        userBalnc,
        setUserBalnc,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
